import { useState, type FormEvent, type ReactNode } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import CssBaseline from '@mui/material/CssBaseline'
import FormHelperText from '@mui/material/FormHelperText'
import TextField, { type TextFieldProps } from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import { Loading } from './Loading'

export { default as nimbraEdgeLogo } from '../img/logo-edge.svg'
export { default as nimbraEdgeOsLogo } from '../img/logo-edgeos.svg'

const Form = styled('form')(({ theme }) => ({
    width: '100%',
    marginTop: theme.spacing(1),
}))

const LoginImg = styled('img')({
    height: '44px',
    margin: '0 auto',
    display: 'block',
})

const MAX_LENGTH = 255
const maxLengthError = `Maximum ${MAX_LENGTH} characters`

const commonInputProps = {
    variant: 'outlined' as const,
    margin: 'normal' as const,
    required: true,
    fullWidth: true,
    inputProps: { maxLength: MAX_LENGTH },
    InputLabelProps: { shrink: true },
} as const satisfies TextFieldProps

const atMaxLength = (value: string) => value.length >= MAX_LENGTH

/** Error props shown when an input reaches the maxLength limit */
const maxLengthProps = (value: string): Pick<TextFieldProps, 'error' | 'helperText'> =>
    atMaxLength(value) ? { error: true, helperText: maxLengthError } : {}

export interface SsoLoginMethod {
    strategy: string
    displayName: string
}

export interface LoginDialogProps {
    /** Called with credentials when the user submits the form */
    onLogin: (credentials: { username: string; password: string; otp?: string }) => void
    /** Whether a login request is in progress */
    loading?: boolean
    /** Error message to display (e.g. "Invalid credentials") */
    error?: string
    /** Whether MFA input should be shown instead of username/password */
    mfaRequired?: boolean
    /** SSO login methods to display below the form */
    ssoLoginMethods?: SsoLoginMethod[]
    /** Called when an SSO button is clicked */
    onSsoLogin?: (strategy: string) => void
    /** Custom logo element rendered above the title (overrides logoSrc) */
    logo?: ReactNode
    /** Logo image URL rendered at consistent size (ignored if logo is provided) */
    logoSrc?: string
    /** Title text (default: "Sign in") */
    title?: string
}

export const LoginDialog = ({
    onLogin,
    loading,
    error,
    mfaRequired,
    ssoLoginMethods,
    onSsoLogin,
    logo,
    logoSrc,
    title = 'Sign in',
}: LoginDialogProps) => {
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [otp, setOtp] = useState('')

    const limitReached = mfaRequired ? atMaxLength(otp) : atMaxLength(username) || atMaxLength(password)

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault()
        if (limitReached) return
        onLogin({ username, password, otp: mfaRequired ? otp : undefined })
        setOtp('')
    }

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline />
            <Loading loading={loading} fullScreen />
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                {logo ??
                    (logoSrc && (
                        <Box sx={{ width: 396, my: 2 }}>
                            <LoginImg src={logoSrc} alt="Logo" />
                        </Box>
                    ))}
                <Typography component="h1" variant="h5">
                    {title}
                </Typography>
                <Form noValidate onSubmit={handleSubmit}>
                    {!mfaRequired ? (
                        <>
                            <TextField
                                {...commonInputProps}
                                {...maxLengthProps(username)}
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                id="username"
                                label="Username"
                                autoComplete="username"
                                autoFocus
                            />
                            <TextField
                                {...commonInputProps}
                                {...maxLengthProps(password)}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                label="Password"
                                type="password"
                                id="password"
                                autoComplete="current-password"
                            />
                        </>
                    ) : (
                        <TextField
                            {...commonInputProps}
                            {...maxLengthProps(otp)}
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            id="mfacode"
                            label="MFA code"
                            autoComplete="one-time-code"
                            autoFocus
                        />
                    )}
                    {error && !mfaRequired && (
                        <FormHelperText error variant="filled">
                            {error}
                        </FormHelperText>
                    )}
                    <Button
                        id="login-button"
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ margin: (theme) => theme.spacing(3, 0, 2) }}
                        disabled={!username || !password || (mfaRequired && !otp) || limitReached}
                    >
                        Sign In
                    </Button>
                </Form>

                {(ssoLoginMethods ?? []).length > 0 && (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                marginTop: 2,
                                marginBottom: 2,
                                width: '80%',
                            }}
                        >
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'grey.500' }} />
                            <Typography variant="body1" sx={{ marginX: 2 }}>
                                or sign in with
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'grey.500' }} />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                            {(ssoLoginMethods ?? []).map((method) => (
                                <Button
                                    data-testid={`sso-${method.strategy}`}
                                    key={method.strategy}
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => onSsoLogin?.(method.strategy)}
                                    fullWidth
                                >
                                    {method.displayName}
                                </Button>
                            ))}
                        </Box>
                    </>
                )}
            </Box>
        </Container>
    )
}
